import * as XLSX from 'xlsx';
import tableDesign from 'themes/table';
import Table from '@mui/material/Table';
import MainCard from 'components/MainCard';
import { Empty, Flex, Tooltip } from 'antd';
import TableRow from '@mui/material/TableRow';
import { useLocation } from 'react-router-dom';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import LynIcon from 'components/@extended/LynIcon';
import { generateExcelFileName } from 'utils/utils';
import TablePagination from './DataTablePagination';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import ColumnFilter from 'components/@extended/ColumnFilter';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import SortingIconGroup from 'components/@extended/SortingIconGroup';
import SelectVisibleColumn from 'components/@extended/SelectVisibleColumn';
import { setDataTableInfo, setScrollIntoTableRow } from 'store/reducer/mainSlice';
import { useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import ExportButton from 'components/button/ExportButton';

export const stickyBody = {
	left: 0,
	position: 'sticky',
	padding: tableDesign.padding.primary,
	borderLeft: `1px solid ${tableDesign.borderColor.primary} !important`,
};

export const stickyHead = {
	left: 0,
	zIndex: 3,
	position: 'sticky',
	p: tableDesign.padding.default,
	color: tableDesign.color.primary,
	bgcolor: tableDesign.background.primary,
};

export const stickySearch = {
	left: 0,
	zIndex: 3,
	position: 'sticky',
	p: tableDesign.padding.secondary,
	bgcolor: tableDesign.background.primary,

	'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },
};

const tableVariants = {
	default: {
		table: {
			'& td, th': {
				borderRight: 1,
				borderColor: tableDesign.borderColor.primary,
			},

			'& th': {
				'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
			},
			'& td': {
				'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
			},
		},
		tableMainCard: {
			mt: 2,
			width: '100%',
			borderRadius: tableDesign.borderRadius.primary,
			borderColor: tableDesign.borderColor.primary,
			border: 'none',
		},
		tableContainer: (pagination) => ({
			width: '100%',
			display: 'block',
			overflowX: 'auto',
			position: 'relative',
			mb: pagination ? '36px' : '0px',
			maxHeight: `calc(100svh - 330px)`,

			'& td, & th': { whiteSpace: 'nowrap' },
		}),
		tableBodyCell: {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			borderBottom: '1px solid #E0E0E0',
			padding: tableDesign.padding.secondary,

			'&:first-of-type': { borderLeft: '1px solid', borderColor: tableDesign.borderColor.primary },
		},
		tableHeaderCell: {
			p: 0.5,
			top: '49px',
			height: '41px',
			position: 'sticky',
			backgroundColor: tableDesign.background.primary,

			'&:first-of-type': { backgroundColor: tableDesign.background.primary },

			'&:last-child': { borderColor: tableDesign.background.primary },
		},
		tableHeaderSelectionCell: (header) => ({
			padding: tableDesign.padding.secondary,
			backgroundColor: tableDesign.background.primary,
			...(header.id.toLowerCase() === 'selection' && { width: '52px' }),
		}),
		tableBodyCellBox: (cell) => ({
			display: 'block',
			fontSize: tableDesign.fontSize.primary,
			fontWeight: tableDesign.fontWeight.primary,
			maxWidth: '337px',
			overflow: 'hidden',
			padding: '0px',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			textAlign: cell?.column?.cellAlign || 'left',
			color: tableDesign.color.secondary,

			...(cell?.column?.cellAlign === 'right' ? { fontVariantNumeric: 'tabular-nums' } : {}),
		}),
		tableHeaderRow: { height: '48px' },
		tableHeaderSelectionBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
		tableBodyRow: { cursor: 'pointer' },
		emptyTableBox: {
			width: '100%',
			height: '350px',
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		paginationBox: {
			left: 0,
			bottom: 0,
			width: '100%',
			position: 'absolute',
			backgroundColor: tableDesign.background.primary,
		},
		pagination: {
			'&.Mui-disabled': { color: 'red' },
			'&.MuiTablePagination-root': { color: tableDesign.color.primary },
			'.MuiTablePagination-selectIcon': { color: tableDesign.color.primary },
		},
	},
};

function DataTable({
	children,
	DATA = [],
	current = 0,
	COLUMNS = [],
	rowSelection,
	deleteHandle,
	pageSize = 15,
	onClickHandler,
	bulkDelete = true,
	pagination = true,
	variant = 'default',
	activeDeactiveHandle,
	firstColStrick = true,
	xlsxFileName = 'lynorg',
	exportButtonShow = true,
	defaultVisibleColumn = [],
	rowIndexColorMapping = {},
	setRowSelection = () => {},
	columnFilterSelector = true,
	tableUpperLeftExtraInfo = null,
}) {
	const theme = useTheme();
	const location = useLocation();
	const dispatch = useDispatch();

	const { pathname } = location;

	const limit = useSelector((state) => state.main.limit);
	const pageIndex = useSelector((state) => state.main.page);
	const clickedRow = useSelector((state) => state.main.clickedItem);

	const data = useMemo(() => DATA || [], [DATA]);
	const columns = useMemo(() => COLUMNS || [], [COLUMNS]);

	const defaultColumn = useMemo(() => {
		return {
			Filter: ColumnFilter,
			sortDescFirst: true,
		};
	}, []);

	const tableInstance = useTable(
		{
			columns,
			data,
			defaultColumn,
			initialState: {
				pageIndex: current,
				pageSize: pageSize,
				hiddenColumns: defaultVisibleColumn,
			},
			autoResetPage: true,
		},
		useFilters,
		useSortBy,
		usePagination,
		useRowSelect
	);

	const {
		rows,
		page,
		state,
		gotoPage,
		allColumns,
		prepareRow,
		setPageSize,
		headerGroups,
		filteredRows,
		getTableProps,
		getTableBodyProps,
		selectedFlatRows,
		getToggleHideAllColumnsProps,
	} = tableInstance;

	const dataExport = useCallback(() => {
		const headerArr = headerGroups[0].headers.map((item) => item.Header).filter((item) => item !== 'Action' && typeof item !== 'function');

		const rowsArr = rows.map((item) => {
			const disableColumnKey = [...state.hiddenColumns, 'Selection', 'Action'];
			const obj = { ...item.values };
			disableColumnKey.forEach((key) => {
				delete obj[key];
			});
			return Object.values(obj);
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);

		// Add headers
		XLSX.utils.sheet_add_aoa(ws, [headerArr]);

		// Add rows
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });

		// 🔹 Convert formatted numbers (like "754,983.90") into real numbers
		Object.keys(ws).forEach((cell) => {
			if (cell[0] === '!') return; // skip metadata
			const cellObj = ws[cell];
			if (typeof cellObj.v === 'string') {
				// Remove commas for number conversion
				const cleaned = cellObj.v.replace(/,/g, '');
				if (!isNaN(cleaned)) {
					cellObj.v = Number(cleaned); // convert
					cellObj.t = 'n'; // numeric type
					cellObj.z = '#,##0.00'; // 🔹 Excel formatting with commas + 2 decimals
				}
			}
		});

		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateExcelFileName(xlsxFileName)}.xlsx`);
	}, [state.hiddenColumns, headerGroups, rows, xlsxFileName]);

	const memorizeClickedItem = (id) => {
		dispatch(setScrollIntoTableRow({ clickedItem: { [pathname]: id } }));
		dispatch(setDataTableInfo({ page: { [pathname]: state?.pageIndex }, limit: { [pathname]: state?.pageSize } }));
	};

	useEffect(() => {
		setRowSelection(selectedFlatRows);
	}, [selectedFlatRows]);

	useEffect(() => {
		if (state.filters.length === 0) {
			gotoPage(pageIndex?.[pathname] || state?.pageIndex);
		}
		if (pagination) {
			setPageSize(limit?.[pathname] || state?.pageSize);
		} else {
			setPageSize(data?.length || 0);
		}
	}, []);

	useEffect(() => {
		if (!pagination && data?.length) {
			setPageSize(data.length);
		}
	}, [pagination, data?.length]);

	useEffect(() => {
		const id = clickedRow[pathname];
		if (id) {
			const project = document.getElementById(id);
			if (project) {
				project.classList.add('active');
				project.scrollIntoView({ behavior: 'smooth', block: 'center' });
				dispatch(setScrollIntoTableRow({ clickedItem: {} }));
			}
		}
	}, [allColumns, state.pageIndex, state.pageSize]);

	const styles = {
		table: { ...tableVariants.default.table, ...tableVariants[variant]?.table },
		pagination: { ...tableVariants.default.pagination, ...tableVariants[variant]?.pagination },
		tableBodyRow: { ...tableVariants.default.tableBodyRow, ...tableVariants[variant]?.tableBodyRow },
		emptyTableBox: { ...tableVariants.default.emptyTableBox, ...tableVariants[variant]?.emptyTableBox },
		tableMainCard: { ...tableVariants.default.tableMainCard, ...tableVariants[variant]?.tableMainCard },
		paginationBox: { ...tableVariants.default.paginationBox, ...tableVariants[variant]?.paginationBox },
		tableBodyCell: { ...tableVariants.default.tableBodyCell, ...tableVariants[variant]?.tableBodyCell },
		tableHeaderRow: { ...tableVariants.default.tableHeaderRow, ...tableVariants[variant]?.tableHeaderRow },
		tableHeaderCell: { ...tableVariants.default.tableHeaderCell, ...tableVariants[variant]?.tableHeaderCell },
		tableContainer: (pagination) => ({
			...tableVariants.default.tableContainer(pagination),
			...tableVariants[variant]?.tableContainer(pagination),
		}),
		tableBodyCellBox: (cell) => ({
			...tableVariants.default.tableBodyCellBox(cell),
			...tableVariants[variant]?.tableBodyCellBox(cell),
		}),
		tableHeaderSelectionBox: {
			...tableVariants.default.tableHeaderSelectionBox,
			...tableVariants[variant]?.tableHeaderSelectionBox,
		},
		tableHeaderSelectionCell: (header) => ({
			...tableVariants.default.tableHeaderSelectionCell(header),
			...tableVariants[variant]?.tableHeaderSelectionCell(header),
		}),
	};

	return (
		<>
			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<Box display="flex" gap={2} flexDirection="column">
					{tableUpperLeftExtraInfo?.component ? (
						tableUpperLeftExtraInfo.component
					) : tableUpperLeftExtraInfo ? (
						<Box>
							{Object.keys(tableUpperLeftExtraInfo).map((key) => (
								<Box key={key} sx={{ display: 'flex' }}>
									<Typography sx={{ width: '130px', fontWeight: 'bold' }}>
										{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
									</Typography>
									<Typography sx={{ width: '30px' }}>: </Typography>
									<Typography>{tableUpperLeftExtraInfo[key]}</Typography>
								</Box>
							))}
						</Box>
					) : null}

					{columnFilterSelector ? (
						<SelectVisibleColumn
							allColumns={allColumns}
							visibleColumns={headerGroups[0]?.headers}
							getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
						/>
					) : (
						children
					)}
				</Box>

				<Flex justify="space-between" align="center" gap=".5rem">
					{rowSelection?.length > 0 && (
						<>
							<div>
								{/* {rowSelection?.length > 1 ? "Total " : ""} */}
								<b style={{ fontSize: '14px' }}>{rowSelection?.length}</b> {rowSelection?.length > 1 ? 'rows' : 'row'} selected
							</div>

							<Tooltip title={rowSelection?.length > 1 ? 'Activate all' : 'Activate'}>
								<Button
									type="primary"
									style={{
										margin: '3px',
										backgroundColor: theme.palette.lynTheme.success,
										color: 'white',
										padding: '8px 8px',
										fontSize: '14px',
										lineHeight: '18px',
									}}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Inactive');
									}}
								>
									{rowSelection?.length > 1 ? 'Activate all' : 'Activate'}
								</Button>
							</Tooltip>

							<Tooltip title={rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
								<Button
									type="primary"
									style={{
										margin: '3px',
										backgroundColor: theme.palette.lynTheme.submit,
										color: 'white',
										padding: '8px 8px',
										fontSize: '14px',
										lineHeight: '18px',
									}}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Active');
									}}
								>
									{rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}
								</Button>
							</Tooltip>

							{bulkDelete && (
								<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
									<Button
										type="primary"
										style={{
											margin: '3px',
											backgroundColor: theme.palette.lynTheme.delete,
											color: 'white',
											padding: '8px 8px',
											fontSize: '14px',
											lineHeight: '18px',
										}}
										onClick={(e) => {
											e.stopPropagation();
											if (typeof deleteHandle === 'function') {
												let arr = rowSelection?.map((item) => item?.original);
												deleteHandle(arr);
											}
										}}
									>
										{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
									</Button>
								</Tooltip>
							)}
						</>
					)}

					{exportButtonShow && <ExportButton handleDataExport={dataExport} />}
				</Flex>
			</Box>

			<MainCard sx={styles.tableMainCard} content={false}>
				<TableContainer sx={styles.tableContainer(pagination)}>
					<Table stickyHeader {...getTableProps()} sx={styles.table}>
						<TableHead>
							{headerGroups?.map((headerGroup, index) => (
								<TableRow {...headerGroup?.getHeaderGroupProps()} sx={{ height: '40px', th: { userSelect: 'none' } }} key={index}>
									{headerGroup?.headers.map((column, index) => (
										<TableCell
											{...column?.getHeaderProps()}
											sx={
												firstColStrick && index === 0
													? {
															...stickyHead,
															width: column?.width || 'auto',
														}
													: {
															p: tableDesign.padding.default,
															backgroundColor: tableDesign.background.primary,
															color: tableDesign.color.primary,
															width: column?.width || 'auto',
														}
											}
											key={index}
										>
											<Box
												sx={{
													fontSize: tableDesign.headerFontSize.primary,
													fontWeight: tableDesign.headerFontWeight.primary,
													display: 'flex',
													alignItems: 'center',
													justifyContent: column?.align ? column?.align : 'space-between',
												}}
												{...column.getSortByToggleProps()}
											>
												{column?.render('Header')}
												{column?.canSort && <SortingIconGroup sortingProps={column} />}
											</Box>
										</TableCell>
									))}
								</TableRow>
							))}

							{headerGroups?.map((headerGroup, index) => (
								<TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& .MuiTableCell-root': { top: '40px' } }} key={index}>
									{headerGroup.headers.map((column, nestedIndex) =>
										column?.id === 'Selection' || column?.id === 'Sl' ? (
											<TableCell
												sx={{
													backgroundColor: tableDesign.background.primary,
													...(nestedIndex === 0 && { position: 'sticky', left: 0, zIndex: '3' }),
												}}
												key={index + '_' + nestedIndex}
											></TableCell>
										) : (
											<TableCell
												sx={
													firstColStrick && nestedIndex === 0
														? stickySearch
														: {
																p: tableDesign.padding.secondary,
																backgroundColor: tableDesign.background.primary,

																'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },
															}
												}
												{...column.getHeaderProps()}
												key={index + '_' + nestedIndex}
											>
												{column.canFilter ? column.render('Filter') : null}
											</TableCell>
										)
									)}
								</TableRow>
							))}
						</TableHead>

						<TableBody
							{...getTableBodyProps()}
							key="table_body2"
							sx={{
								'& tr:nth-of-type(even)': {
									backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
								},
							}}
						>
							{page?.length > 0 ? (
								page?.map((row, index) => {
									prepareRow(row);
									return (
										<TableRow
											// hover
											id={row.id}
											{...row?.getRowProps()}
											sx={{
												cursor: 'pointer',
											}}
											onClick={() => memorizeClickedItem(row.id)}
											key={index}
										>
											{row?.cells?.map((cell, index) => (
												<TableCell
													sx={
														index === 0 && firstColStrick
															? stickyBody
															: { borderBottom: '1px solid #E0E0E0', p: tableDesign.padding.primary }
													}
													{...cell.getCellProps()}
													onClick={() => {
														if (!row?.cells[index]?.column?.onClicked) {
															onClickHandler(row.original);
														}
													}}
													key={index}
												>
													{cell?.column?.id === 'Selection' || cell?.column?.id === 'Sl' ? (
														<Box
															component="div"
															sx={{
																textAlign: cell?.column?.cellAlign || 'left',
															}}
														>
															{cell.render('Cell')}
														</Box>
													) : (
														<Box component="div" sx={styles.tableBodyCellBox(cell)}>
															{cell.render('Cell')}
														</Box>
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={columns?.length} sx={stickyBody}>
										<Box component="div" sx={styles.emptyTableBox}>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{pagination && (
					<Box sx={styles.paginationBox}>
						<TablePagination
							sx={styles.pagination}
							{...{
								gotoPage: gotoPage,
								setPageSize: setPageSize,
								state: state,
								count: filteredRows?.length,
								pathname,
							}}
						/>
					</Box>
				)}
			</MainCard>
		</>
	);
}

export default React.memo(DataTable);
